import { ToggleLeft, ToggleRight, FileText, Bell, Truck } from 'lucide-react';
import { INDIAN_STATES } from './indianStates';

const inputCls = 'w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500 bg-white text-slate-800';

const INVOICE_TOGGLES = [
  ['invoice_show_seller_gstin', 'Seller GSTIN', 'Show your GSTIN in the invoice header and tax box.'],
  ['invoice_show_seller_address', 'Seller address', 'Show your business address under the logo.'],
  ['invoice_show_gst_details', 'GST details box', 'GSTIN, place of supply, supply type and applicable GST.'],
  ['invoice_show_hsn', 'HSN code', 'Show the product HSN code under each item name.'],
  ['invoice_show_sku', 'SKU column', 'Show the SKU column in the items table.'],
  ['invoice_show_tax_columns', 'Tax % and tax amount columns', 'Per-item tax rate and tax amount in the items table.'],
  ['invoice_show_tax_breakup', 'CGST / SGST / IGST breakup', 'Tax split in the totals block.'],
  ['invoice_show_payment', 'Payment method', 'Show the payment method next to the ship-to address.'],
];

const LABEL_TOGGLES = [
  ['label_show_return_address', 'Return address', 'Print your business address as the sender / return address.'],
  ['label_show_seller_phone', 'Your phone number', 'Print your phone number with the return address.'],
  ['label_show_cod_amount', 'COD amount to collect', 'For cash-on-delivery orders, print the amount the courier must collect.'],
  ['label_show_items', 'Item list', 'Print each item, variant, SKU and quantity on the label.'],
];

function Header({ icon: Icon, title, subtitle }) {
  return (
    <div className='flex items-center gap-3 px-5 py-4 border-b border-slate-100'>
      <div className='w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center'>
        <Icon className='w-4 h-4 text-orange-600' />
      </div>
      <div>
        <p className='text-sm font-bold text-slate-900'>{title}</p>
        {subtitle && <p className='text-xs text-slate-500'>{subtitle}</p>}
      </div>
    </div>
  );
}

function Toggle({ label, description, value, onChange, testId }) {
  return (
    <div className='flex items-start justify-between gap-4 py-3'>
      <div className='flex-1 min-w-0'>
        <p className='text-sm font-semibold text-slate-800'>{label}</p>
        {description && <p className='text-xs text-slate-500 mt-0.5 leading-relaxed'>{description}</p>}
      </div>
      <button
        data-testid={testId}
        onClick={() => onChange(!value)}
        className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all border ${
          value ? 'bg-orange-500 text-white border-orange-500 hover:bg-orange-600' : 'bg-white text-slate-500 border-slate-300 hover:border-slate-400'
        }`}
      >
        {value ? <><ToggleRight className='w-3.5 h-3.5' /> Shown</> : <><ToggleLeft className='w-3.5 h-3.5' /> Hidden</>}
      </button>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className='block'>
      <span className='block text-xs font-semibold text-slate-600 mb-1'>{label}</span>
      {children}
      {hint && <span className='block text-[11px] text-slate-400 mt-1'>{hint}</span>}
    </label>
  );
}

export default function InvoiceSettingsSection({ settings, onChange }) {
  const s = settings || {};
  const text = (key, label, hint, placeholder) => (
    <Field label={label} hint={hint}>
      <input
        data-testid={`setting-${key}`}
        className={inputCls}
        value={s[key] ?? ''}
        placeholder={placeholder}
        onChange={(e) => onChange(key, e.target.value)}
      />
    </Field>
  );

  return (
    <>
      <div className='bg-white border border-slate-200 rounded-xl overflow-hidden mb-4' data-testid='invoice-settings'>
        <Header icon={FileText} title='Invoice & Business Details' subtitle='Printed on every invoice from Orders > Print Invoice' />
        <div className='px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-4'>
          {text('seller_name', 'Business name')}
          {text('seller_gstin', 'GSTIN', 'Your 15-character GST number', '22AAAAA0000A1Z5')}
          {text('seller_pan', 'PAN')}
          {text('seller_phone', 'Phone')}
          {text('seller_email', 'Email')}
          {text('invoice_prefix', 'Invoice number prefix', 'Invoice number = prefix + order number, e.g. INV-IMP2026110005')}
          <Field label='Default GST rate (%)' hint='Used only for products that have no Tax Rate set. Each product uses its own Tax Rate from Products > Basic Info.'>
            <select data-testid='setting-invoice_default_tax_rate' className={inputCls} value={String(s.invoice_default_tax_rate ?? 0)} onChange={(e) => onChange('invoice_default_tax_rate', Number(e.target.value))}>
              {[0, 3, 5, 12, 18, 28].map((r) => <option key={r} value={String(r)}>{r}%</option>)}
            </select>
          </Field>
          <div className='sm:col-span-2'>{text('seller_address', 'Address')}</div>
          {text('seller_city', 'City')}
          <Field label='State' hint='Used to decide CGST + SGST (same state) or IGST (different state)'>
            <select data-testid='setting-seller_state' className={inputCls} value={s.seller_state ?? ''} onChange={(e) => onChange('seller_state', e.target.value)}>
              <option value=''>Select state</option>
              {INDIAN_STATES.map((st) => <option key={st.code} value={st.name}>{st.name}</option>)}
            </select>
          </Field>
          {text('seller_pincode', 'Pincode')}
          <div className='sm:col-span-2'>{text('invoice_tax_note', 'Tax information note')}</div>
          <div className='sm:col-span-2'>{text('invoice_footer_note', 'Footer note')}</div>
        </div>
        <div className='px-5 divide-y divide-slate-100 border-t border-slate-100'>
          {INVOICE_TOGGLES.map(([key, label, description]) => (
            <Toggle key={key} label={label} description={description} value={s[key] ?? true} onChange={(v) => onChange(key, v)} testId={`setting-${key}`} />
          ))}
        </div>
      </div>

      <div className='bg-white border border-slate-200 rounded-xl overflow-hidden mb-4' data-testid='shipping-label-settings'>
        <Header icon={Truck} title='Shipping Label' subtitle='Printed from Orders > open an order > Documents > Print Shipping Label' />
        <div className='px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-4'>
          <Field label='Label size' hint='4 x 6 inch fits thermal label printers. A4 prints the label on a normal sheet.'>
            <select data-testid='setting-label_size' className={inputCls} value={s.label_size || '4x6'} onChange={(e) => onChange('label_size', e.target.value)}>
              <option value='4x6'>4 x 6 inch (thermal)</option>
              <option value='a4'>A4 sheet</option>
            </select>
          </Field>
        </div>
        <div className='px-5 divide-y divide-slate-100 border-t border-slate-100'>
          {LABEL_TOGGLES.map(([key, label, description]) => (
            <Toggle key={key} label={label} description={description} value={s[key] ?? true} onChange={(v) => onChange(key, v)} testId={`setting-${key}`} />
          ))}
        </div>
      </div>

      <div className='bg-white border border-slate-200 rounded-xl overflow-hidden mb-6' data-testid='order-alert-settings'>
        <Header icon={Bell} title='New Order Alerts' subtitle='Email sent to you each time a customer places an order' />
        <div className='px-5 divide-y divide-slate-100'>
          <Toggle
            label='Email me on every new order'
            value={s.notify_new_order_enabled ?? true}
            onChange={(v) => onChange('notify_new_order_enabled', v)}
            testId='setting-notify_new_order_enabled'
          />
          <div className='py-4'>
            {text('notify_new_order_email', 'Alert email address', 'New-order emails go to this address. If blank, the business email above is used. If both are blank, no email is sent.', 'you@yourbusiness.com')}
          </div>
        </div>
      </div>
    </>
  );
}
